import React, { useEffect, useRef } from 'react';
import anime from 'animejs';
import { SpeedUnit } from '../../types';

interface SpeedometerProps {
  speed: number;
  speedUnit: SpeedUnit;
  acceleration: number;
  maxSpeed?: number;
}

export const EvEcoSpeedometer: React.FC<SpeedometerProps> = ({
  speed,
  speedUnit,
  acceleration,
  maxSpeed = 220,
}) => {
  const energyFieldRef = useRef<HTMLDivElement | null>(null);
  const speedTextRef = useRef<HTMLSpanElement | null>(null);
  const prevSpeedRef = useRef<number>(0);

  const clampedSpeed = Math.max(0, Math.min(maxSpeed, speed));
  const energyPct = Math.round((clampedSpeed / maxSpeed) * 100);

  useEffect(() => {
    if (!speedTextRef.current) return;
    const obj = { val: prevSpeedRef.current };
    anime({
      targets: obj,
      val: speed,
      round: 1,
      duration: 650,
      easing: 'easeOutSine',
      update: () => {
        if (speedTextRef.current) {
          speedTextRef.current.textContent = String(Math.round(obj.val));
        }
      },
    });
    prevSpeedRef.current = speed;
  }, [speed]);

  useEffect(() => {
    if (!energyFieldRef.current) return;
    const accelBoost = Math.max(0, acceleration) * 2000;
    anime({
      targets: energyFieldRef.current,
      rotate: '+=360deg',
      duration: Math.max(1200, 10000 - clampedSpeed * 35 - accelBoost),
      easing: 'linear',
      loop: true,
    });
  }, [clampedSpeed, acceleration]);

  return (
    <div className="relative w-72 h-72 md:w-84 md:h-84 flex flex-col items-center justify-center select-none">
      {/* Organic Pulsing Green/Teal Energy Field */}
      <div
        className="absolute inset-6 rounded-full opacity-60 blur-3xl transition-all duration-500 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(16,185,129,0.3) 0%, rgba(20,184,166,0.1) 70%, transparent 100%)',
          transform: `scale(${1.0 + (energyPct / 250)})`,
        }}
      />

      {/* Rotating Energy Field Particles SVG */}
      <div ref={energyFieldRef} className="absolute inset-0 w-full h-full pointer-events-none">
        <svg className="w-full h-full" viewBox="0 0 300 300">
          <circle
            cx="150"
            cy="150"
            r="120"
            fill="none"
            stroke="rgba(16, 185, 129, 0.2)"
            strokeWidth="1.5"
            strokeDasharray="4 8"
          />
          <circle
            cx="150"
            cy="150"
            r="132"
            fill="none"
            stroke="rgba(20, 184, 166, 0.3)"
            strokeWidth="2"
            strokeDasharray="12 24"
          />
        </svg>
      </div>

      {/* Central Futuristic Digital Display */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center">
        <div className="flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-emerald-950/60 border border-emerald-500/30 mb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[10px] font-mono tracking-[0.2em] text-emerald-300 font-bold uppercase">
            ENERGY HARVEST • {energyPct}%
          </span>
        </div>

        <span
          ref={speedTextRef}
          className="text-7xl md:text-8xl font-black font-display text-white tracking-tight drop-shadow-[0_4px_20px_rgba(16,185,129,0.3)] my-1"
        >
          {Math.round(speed)}
        </span>

        <span className="text-xs font-mono tracking-widest text-emerald-400 font-black uppercase">
          {speedUnit.toUpperCase()}
        </span>

        <span className="text-[9px] font-mono text-slate-400 tracking-widest uppercase mt-3">
          ZERO TRANSMISSION • PURE FLOW
        </span>
      </div>
    </div>
  );
};
